import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Upload, Download, Trash2, Edit2, Copy, FileText, LogIn,
  CheckCircle2, XCircle, AlertTriangle, ShieldCheck, HelpCircle, Info,
  Pin, PinOff,
} from 'lucide-react';
import { applySuperscripts } from '../../utils/automataHelpers';
import AuthDialog from '../AuthDialog';

export function DashboardDialogs({
  // Rename
  showRenameDialog, setShowRenameDialog, renameName, setRenameName, onRenameSubmit,
  // Sidebar rename
  showSidebarRenameDialog, setShowSidebarRenameDialog, sidebarRenameName, setSidebarRenameName, onSidebarRenameSubmit,
  // Import
  showImportDialog, setShowImportDialog, importJson, setImportJson, importGroupId, setImportGroupId,
  groups, onImportSubmit, onImportFile,
  // Import warning (guest)
  showImportWarningDialog, setShowImportWarningDialog,
  onExportThenImport, onSignInThenImport, onImportAnyway,
  // Clear canvas (guest)
  showClearCanvasDialog, setShowClearCanvasDialog, onClearCanvas, onExportAndClear,
  // Auth
  showAuthDialog, setShowAuthDialog, onAuthSuccess, onGoogleAuth,
  // Verify
  showVerifyDialog, setShowVerifyDialog, verificationResult,
  verifyMode, setVerifyMode, verifyAutomaton,
  // New group
  showNewGroupDialog, setShowNewGroupDialog, createGroup,
  // Group rename
  showGroupRenameDialog, setShowGroupRenameDialog,
  groupRenameId, groupRenameName, setGroupRenameName, renameGroup, deleteGroup,
  // Context menu
  contextMenu, onCloseContextMenu,
  automata,
  onContextRename, onContextDuplicate, onContextExport, onContextSaveDocx, onContextDelete, onContextTogglePin,
  isReadOnly = false,
}) {
  const [newGroupName, setNewGroupName] = useState('');
  const contextAutomaton = automata?.find(a => a.automaton_id === contextMenu.automatonId);
  const isContextPinned = !!contextAutomaton?.pinned;
  const disabledMenuItemClass = 'opacity-40 pointer-events-none cursor-not-allowed';

  return (
    <>
      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Automaton</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename-input">Name</Label>
            <Input
              id="rename-input"
              value={renameName}
              onChange={(e) => setRenameName(applySuperscripts(e.target.value))}
              data-testid="rename-input"
              autoFocus
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => { if (e.key === 'Enter') onRenameSubmit(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>Cancel</Button>
            <Button onClick={onRenameSubmit} data-testid="rename-submit-btn">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sidebar Rename Dialog */}
      <Dialog open={showSidebarRenameDialog} onOpenChange={setShowSidebarRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Automaton</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="sidebar-rename-input">Name</Label>
            <Input
              id="sidebar-rename-input"
              value={sidebarRenameName}
              onChange={(e) => setSidebarRenameName(applySuperscripts(e.target.value))}
              data-testid="sidebar-rename-input"
              autoFocus
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => { if (e.key === 'Enter') onSidebarRenameSubmit(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSidebarRenameDialog(false)}>Cancel</Button>
            <Button onClick={onSidebarRenameSubmit} data-testid="sidebar-rename-submit-btn">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Warning Dialog (Guest) */}
      <Dialog open={showImportWarningDialog} onOpenChange={setShowImportWarningDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Warning</DialogTitle>
            <DialogDescription>
              You have unsaved work. Since you're in guest mode, importing will replace your current automaton.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowImportWarningDialog(false)} data-testid="import-warn-cancel-btn">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onExportThenImport} data-testid="import-warn-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export &amp; Import
            </Button>
            <Button variant="secondary" onClick={onSignInThenImport} data-testid="import-warn-signin-btn">
              <LogIn className="w-4 h-4 mr-1.5" /> Sign In to Save
            </Button>
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={onImportAnyway}
              data-testid="import-warn-proceed-btn"
            >
              Import Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => { setShowImportDialog(open); if (!open) setImportGroupId(null); }}>
        <DialogContent className="max-w-lg" onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <DialogHeader>
            <DialogTitle>
              Import Automaton{importGroupId && groups.length > 0 ? ` into ${groups.find(g => g.group_id === importGroupId)?.name || ''}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <Label htmlFor="import-file" className="cursor-pointer">
                <div
                  className="flex items-center gap-2 border border-dashed border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('bg-muted/80', 'border-primary'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('bg-muted/80', 'border-primary'); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    e.currentTarget.classList.remove('bg-muted/80', 'border-primary');
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.name.endsWith('.json')) {
                      const reader = new FileReader();
                      reader.onload = (ev) => setImportJson(ev.target.result);
                      reader.readAsText(file);
                    } else {
                      toast.error('Please drop a .json file');
                    }
                  }}
                >
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Choose a .json file or drag &amp; drop</span>
                </div>
              </Label>
              <input id="import-file" type="file" accept=".json" className="hidden" onChange={onImportFile} data-testid="import-file-input" />
            </div>
            <div className="text-xs text-center text-muted-foreground">or paste JSON below</div>
            <Textarea
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder='{"name": "My DFA", "type": "DFA", "states": [], ...}'
              className="font-mono text-sm h-48"
              data-testid="import-json-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
            <Button onClick={onImportSubmit} disabled={!importJson.trim()} data-testid="import-submit-btn">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Canvas Dialog (Guest) */}
      <Dialog open={showClearCanvasDialog} onOpenChange={setShowClearCanvasDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Automaton</DialogTitle>
            <DialogDescription>
              This will erase your current automaton. Since you're in guest mode, unsaved work cannot be recovered.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowClearCanvasDialog(false)} data-testid="clear-canvas-cancel-btn">Cancel</Button>
            <div className="flex gap-2 sm:ml-auto">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onExportAndClear} data-testid="export-and-clear-btn">
                <Download className="w-4 h-4 mr-1.5" /> Export &amp; Start New
              </Button>
              <Button variant="destructive" onClick={onClearCanvas} data-testid="clear-canvas-confirm-btn">
                Discard &amp; Start New
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auth Dialog */}
      <AuthDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
        onSuccess={onAuthSuccess}
        onGoogleAuth={onGoogleAuth}
      />

      {/* Verification Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> Automaton Verification
            </DialogTitle>
            <DialogDescription>Checking the current automaton against DFA criteria</DialogDescription>
          </DialogHeader>
          {verificationResult && (
            <div className="space-y-4">
              <div className={`flex items-center gap-3 p-3 rounded-lg ${
                verificationResult.is_dfa
                  ? 'bg-green-500/10 border border-green-500/20'
                  : 'bg-amber-500/10 border border-amber-500/20'
              }`} data-testid="verify-result-badge">
                {verificationResult.is_dfa
                  ? <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" />
                  : <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0" />
                }
                <div>
                  <div className="font-semibold">{verificationResult.is_dfa ? 'Valid DFA' : 'This is an NFA'}</div>
                  <div className="text-xs text-muted-foreground">
                    {verificationResult.is_dfa ? 'This automaton satisfies all DFA requirements' : 'This automaton does not meet DFA criteria'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Completeness:</span>
                <Select value={verifyMode} onValueChange={(v) => {
                  setVerifyMode(v);
                  verifyAutomaton(v);
                }}>
                  <SelectTrigger className="w-28 h-8" data-testid="verify-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strict">Strict</SelectItem>
                    <SelectItem value="loose">Loose</SelectItem>
                  </SelectContent>
                </Select>
                <Tooltip>
                  <TooltipTrigger><HelpCircle className="w-4 h-4 text-muted-foreground" /></TooltipTrigger>
                  <TooltipContent className="max-w-[250px]">
                    <p><strong>Strict:</strong> Every state must have a transition for every alphabet symbol.</p>
                    <p className="mt-1"><strong>Loose:</strong> Missing transitions implicitly go to a dead state.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {verificationResult.issues.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Issues Found:</h4>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="verify-issues-list">
                    {verificationResult.issues.map((issue, i) => (
                      <div key={i} className={`flex items-start gap-2 text-xs p-2 rounded ${
                        issue.type === 'error' ? 'bg-red-500/10 text-red-700 dark:text-red-400' :
                        issue.type === 'warning' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400' :
                        'bg-blue-500/10 text-blue-700 dark:text-blue-400'
                      }`} data-testid={`verify-issue-${i}`}>
                        {issue.type === 'error' ? <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> :
                         issue.type === 'warning' ? <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> :
                         <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
                        <span>{issue.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="verify-no-issues">No issues found.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVerifyDialog(false)} data-testid="verify-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Group Dialog */}
      <Dialog open={showNewGroupDialog} onOpenChange={(open) => { setShowNewGroupDialog(open); if (!open) setNewGroupName(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Group</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-group-input">Group Name</Label>
            <Input
              id="new-group-input"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="e.g., Chapter 1 HW"
              autoFocus
              data-testid="new-group-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newGroupName.trim()) {
                  createGroup(newGroupName.trim());
                  setShowNewGroupDialog(false);
                  setNewGroupName('');
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewGroupDialog(false)}>Cancel</Button>
            <Button
              onClick={() => { createGroup(newGroupName.trim()); setShowNewGroupDialog(false); setNewGroupName(''); }}
              disabled={!newGroupName.trim()}
              data-testid="new-group-submit-btn"
            >Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Rename Dialog */}
      <Dialog open={showGroupRenameDialog} onOpenChange={setShowGroupRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Group</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="group-rename-input">Name</Label>
              <Input
                id="group-rename-input"
                value={groupRenameName}
                onChange={(e) => setGroupRenameName(e.target.value)}
                autoFocus
                onFocus={(e) => e.target.select()}
                data-testid="group-rename-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && groupRenameName.trim()) {
                    renameGroup(groupRenameId, groupRenameName.trim());
                    setShowGroupRenameDialog(false);
                  }
                }}
              />
            </div>
            {groups.length > 1 && (
              <Button
                variant="destructive" size="sm" className="w-full"
                onClick={() => {
                  if (window.confirm(`Delete group "${groupRenameName}"? Automata will be moved to another group.`)) {
                    deleteGroup(groupRenameId);
                    setShowGroupRenameDialog(false);
                  }
                }}
                data-testid="delete-group-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete Group
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupRenameDialog(false)}>Cancel</Button>
            <Button
              onClick={() => { renameGroup(groupRenameId, groupRenameName.trim()); setShowGroupRenameDialog(false); }}
              disabled={!groupRenameName.trim()}
              data-testid="group-rename-submit-btn"
            >Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Right-click Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed z-50 min-w-[160px] bg-popover border border-border rounded-md shadow-md py-1 animate-in fade-in-0 zoom-in-95"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          data-testid="automaton-context-menu"
        >
          <button className={`w-full flex items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${isReadOnly ? disabledMenuItemClass : ''}`} onClick={() => { if (isReadOnly) return; onContextTogglePin(contextMenu.automatonId); onCloseContextMenu(); }} data-testid="context-menu-pin">
            {isContextPinned ? <PinOff className="w-4 h-4 mr-2" /> : <Pin className="w-4 h-4 mr-2" />} {isContextPinned ? 'Unpin' : 'Pin to top'}
          </button>
          <button className={`w-full flex items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${isReadOnly ? disabledMenuItemClass : ''}`} onClick={() => { if (isReadOnly) return; onContextRename(contextMenu.automatonId); onCloseContextMenu(); }} data-testid="context-menu-rename">
            <Edit2 className="w-4 h-4 mr-2" /> Rename
          </button>
          <button className={`w-full flex items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${isReadOnly ? disabledMenuItemClass : ''}`} onClick={() => { if (isReadOnly) return; onContextDuplicate(contextMenu.automatonId); onCloseContextMenu(); }} data-testid="context-menu-duplicate">
            <Copy className="w-4 h-4 mr-2" /> Duplicate
          </button>
          <button className="w-full flex items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors" onClick={() => { onContextExport(contextMenu.automatonId); onCloseContextMenu(); }} data-testid="context-menu-export">
            <Download className="w-4 h-4 mr-2" /> Export
          </button>
          <button className="w-full flex items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors" onClick={() => { onContextSaveDocx(contextMenu.automatonId); onCloseContextMenu(); }} data-testid="context-menu-save-docx">
            <FileText className="w-4 h-4 mr-2" /> Save as Word Document
          </button>
          <div className="h-px bg-border my-1" />
          <button className={`w-full flex items-center px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors ${isReadOnly ? disabledMenuItemClass : ''}`} onClick={() => { if (isReadOnly) return; onContextDelete(contextMenu.automatonId); onCloseContextMenu(); }} data-testid="context-menu-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </button>
        </div>
      )}
    </>
  );
}
